import React from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { ProductKeys } from "@atlassiansox/asc-stargate";

import { useSiteSelectorContext } from "@/components/site-selector-modal/site-selector-context";
import { Button } from "@/components/wpl/button/src/button";
import { ContentBlock } from "@/components/wpl/content-block/src/content-block";
import { Picture } from "@/components/wpl/image/src/image";
import { TabGroup } from "@/components/wpl/tab-group/src/tab-group";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import {
    getKanbanSectionImage,
    getScrumSectionImage
} from "@/templates/software/jira/features/jira-software-features-page/src/localized-images";

interface ScrumKanbanTabProps {
    headerText: string | React.ReactNode;
    headerType?: "h1" | "h2" | "h3" | "h4" | "h5" | undefined;
    bodyText: string | React.ReactNode;
    linkText: string;
    boardLogoImage: string;
    boardLogoImageAltText: string;
    imageLink: string;
    imageAltText: string;
    className?: string;
    linkClassName?: string;
    tabName: "Scrum" | "Kanban";
}

const sectionStyle = css({
    "padding": "80px 0",
    "@media (min-width: 768px) and (max-width: 1023.98px)": {
        padding: "64px 0"
    },
    "@media (max-width: 767.98px)": {
        padding: "64px 0"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a": {
        fontWeight: "500"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".board-image img": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        filter: "drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.3))"
    }
});

const imageLogoStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        width: "40%"
    },
    "@media (max-width: 1023px)": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            display: "none"
        }
    }
});

export const ScrumKanbanTabComponent = ({
    headerText,
    headerType = "h2",
    bodyText,
    linkText,
    boardLogoImage,
    boardLogoImageAltText,
    imageLink,
    imageAltText,
    className = "",
    linkClassName = "",
    tabName
}: ScrumKanbanTabProps) => {
    const { openModalForTemplate } = useSiteSelectorContext();
    const handleLinkClick = (event: React.MouseEvent<Element, MouseEvent>) => {
        event.preventDefault();
        openModalForTemplate({
            templateId:
                "com.pyxis.greenhopper.jira:gh-simplified-agility-scrum",
            templateName: tabName,
            productKey: ProductKeys.JIRA_SOFTWARE
        });
    };

    return (
        <div className={className} css={sectionStyle}>
            <Row layout="4-8">
                <div css={imageLogoStyle}>
                    <Picture
                        image={{
                            src: boardLogoImage,
                            alt: boardLogoImageAltText
                        }}
                    />
                    <ContentBlock
                        headerText={headerText}
                        headerType={headerType}
                        bodyText={bodyText}
                    />
                    {linkText && (
                        <Button
                            type="secondary"
                            text={linkText}
                            size="small"
                            className={linkClassName}
                            onClick={handleLinkClick}
                            dataEventProps={{
                                "data-product-key": "jiraSoftwareFeature",
                                "data-event-component": "linkButton",
                                "data-event-container": "featuresSection",
                                "data-event": "clicked",
                                "data-label": linkText,
                                "data-label-english": `Start ${tabName.toLowerCase()} project`
                            }}
                        />
                    )}
                </div>
                <div className="board-image">
                    <Picture
                        image={{
                            src: imageLink,
                            alt: imageAltText
                        }}
                    />
                </div>
            </Row>
        </div>
    );
};

/* Tabs */
const ScrumBoard = () => {
    const intl = useIntl();
    const scrumBoardImgSrc = getScrumSectionImage(intl.locale);

    const title = intl.formatMessage({
        id: "JiraFeatureSoftwarePageTemplate.tabs.scrum.heading",
        defaultMessage: "Scrum boards"
    });

    const bodyText = intl.formatMessage({
        id: "JiraFeatureSoftwarePageTemplate.tabs.scrum.bodyText",
        defaultMessage:
            "Scrum boards help agile teams break large, complex projects into manageable pieces of work so focused teams, working in sprints, ship faster."
    });

    const scrumLinkText = intl.formatMessage({
        id: "JiraFeatureSoftwarePageTemplate.tabs.scrum.linkText",
        defaultMessage: "Start scrum project"
    });

    const scrumLogoImgSrc =
        "https://wac-cdn.atlassian.com/dam/jcr:9567a40e-6541-4905-bf08-dfe201aa80e7/illustrations-spot-Agile.svg";

    const scrumLogoImgAltText = intl.formatMessage({
        id: "JiraFeatureSoftwarePageTemplate.tabs.scrum.logo.altText",
        defaultMessage: "Scrum logo"
    });

    return (
        <ScrumKanbanTabComponent
            headerText={title}
            headerType="h2"
            bodyText={bodyText}
            linkText={scrumLinkText}
            boardLogoImage={scrumLogoImgSrc}
            boardLogoImageAltText={scrumLogoImgAltText}
            imageLink={scrumBoardImgSrc}
            imageAltText=""
            className="scrum-tab"
            linkClassName="scrum-tab-link"
            tabName="Scrum"
        />
    );
};

const KanbanBoard = () => {
    const intl = useIntl();
    const kanbanBoardImgSrc = getKanbanSectionImage(intl.locale);

    const title = intl.formatMessage({
        id: "JiraFeatureSoftwarePageTemplate.tabs.kanban.heading",
        defaultMessage: "Kanban boards"
    });

    const bodyText = intl.formatMessage({
        id: "JiraFeatureSoftwarePageTemplate.tabs.kanban.bodyText",
        defaultMessage:
            "Flexible kanban boards help teams visualize their workflow, limit work-in-progress, and maximize efficiency as a team. Commonly used by agile and DevOps teams to drive continuous delivery and improvement, get started quickly with a template and customize as you go."
    });

    const kanbanLinkText = intl.formatMessage({
        id: "JiraFeatureSoftwarePageTemplate.tabs.kanban.linkText",
        defaultMessage: "Start kanban project"
    });

    const kanbanLogoImgSrc =
        "https://wac-cdn.atlassian.com/dam/jcr:7d0e6516-361d-4ec3-bd0f-22d7c7d105b4/illustrations-spot-JIRA%20Board.svg?cdnVersion=1403";

    const kanbanLogoImgAltText = intl.formatMessage({
        id: "JiraFeatureSoftwarePageTemplate.tabs.kanban.logo.altText",
        defaultMessage: "Kanban logo"
    });

    return (
        <ScrumKanbanTabComponent
            headerText={title}
            headerType="h2"
            bodyText={bodyText}
            linkText={kanbanLinkText}
            boardLogoImage={kanbanLogoImgSrc}
            boardLogoImageAltText={kanbanLogoImgAltText}
            imageLink={kanbanBoardImgSrc}
            imageAltText=""
            className="kanban-tab"
            linkClassName="kanban-tab-link"
            tabName="Kanban"
        />
    );
};

const scrumKanbanBoardContainerStyle = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundImage: "linear-gradient(180deg, #DEEBFF 57.29%, #B3D4FF 100%)",
    backgroundPosition: "center",
    backgroundrepeat: "no-repeat",
    backgroundSize: "cover"
});

const tabSectionTitleStyles = css({
    "textAlign": "center",
    "fontSize": "1.5rem",
    "marginBlockEnd": "25px",
    "fontWeight": "500",
    "color": "#253858",
    "@media(min-width: 768px)": {
        fontSize: "2.25rem"
    }
});

const tabPillsOverride = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-group-inner button:hover": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        borderColor: "#c1c7d0",
        borderWidth: "1px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-group-inner button": {
        fontSize: "1.25rem",
        borderWidth: "1px"
    }
});

export const ScrumKanbanBoardSection = () => {
    const intl = useIntl();

    const tabsProps = {
        dataProductKey: "jiraSoftwareFeature" as ProductKeyTypes,
        headerText: intl.formatMessage({
            id: "JiraFeatureSoftwarePageTemplate.tabs.heading",
            defaultMessage: "Manage your work with powerful agile boards"
        }),
        tabStyles: {
            // eslint-disable-next-line local/ensure-wac-token-usage
            defaultBG: "#f4f5f7",
            // eslint-disable-next-line local/ensure-wac-token-usage
            defaultText: "#5e6c84",
            // eslint-disable-next-line local/ensure-wac-token-usage
            hoverBG: "#f4f5f7",
            // eslint-disable-next-line local/ensure-wac-token-usage
            hoverText: "#5e6c84",
            // eslint-disable-next-line local/ensure-wac-token-usage
            activeBG: "#fff",
            // eslint-disable-next-line local/ensure-wac-token-usage
            activeText: "#091e42"
        },
        styles: {
            "--lp-tabs-hover": "#deebff",
            "--lp-tabs-unselected": "#6b778c"
        },
        tabs: [
            {
                textLabel: intl.formatMessage({
                    id: "JiraFeatureSoftwarePageTemplate.label.scrum",
                    defaultMessage: "Scrum"
                }),
                id: "scrum",
                component: ScrumBoard()
            },
            {
                textLabel: intl.formatMessage({
                    id: "JiraFeatureSoftwarePageTemplate.label.kanban",
                    defaultMessage: "Kanban"
                }),
                id: "kanban",
                component: KanbanBoard()
            }
        ]
    };

    return (
        <div css={scrumKanbanBoardContainerStyle}>
            <Row layout="8">
                <h2 css={tabSectionTitleStyles}>{tabsProps.headerText}</h2>
            </Row>
            <div css={tabPillsOverride}>
                {/* @ts-ignore */}
                <TabGroup
                    tabContentType="custom"
                    tabs={tabsProps.tabs}
                    id="tabs"
                    label="tabs"
                    colors={tabsProps.tabStyles}
                    dataProductKey={tabsProps.dataProductKey}
                />
            </div>
        </div>
    );
};

export default ScrumKanbanBoardSection;
